import type { ActionMenuItem } from '@/shared/components';
import { ROLES } from '@/shared/auth';
import type { UserResponse } from '@/shared/api';
import { userRole, userStatus } from './userStatus';

export interface UserRowHandlers {
  onRename: (u: UserResponse) => void;
  onChangeRole: (u: UserResponse) => void;
  onAssign: (u: UserResponse) => void;
  onResend: (id: string) => void;
  onDeactivate: (id: string) => void;
  onReactivate: (id: string) => void;
  onResetTwoFactor: (u: UserResponse) => void;
}

/**
 * Das Aktionsmenü einer Zeile — nur Einträge, die der Server für diesen Zustand annimmt.
 *
 * <p>Gäste entstehen über das Gastportal und nicht über die Benutzerverwaltung; „Zugriff verwalten"
 * würde sie still zu Mitarbeitern befördern. „Einladung erneut senden" gibt es nur, solange
 * die Einladung offen ist, und die eigene Zwei-Faktor-Anmeldung setzt niemand über die
 * Benutzerverwaltung zurück.</p>
 */
export function userRowActions(
  u: UserResponse,
  handlers: UserRowHandlers,
  currentUserId?: string,
): ActionMenuItem[] {
  const canChangeRole = userRole(u) !== ROLES.Guest;
  return [
    { label: 'Namen bearbeiten', icon: 'Edit', onClick: () => handlers.onRename(u) },
    ...(canChangeRole
      ? [
          {
            label: 'Zugriff verwalten',
            icon: 'Shield' as const,
            onClick: () => handlers.onChangeRole(u),
          },
        ]
      : []),
    { label: 'Apartments zuweisen', icon: 'Building', onClick: () => handlers.onAssign(u) },
    { separator: true },
    ...(userStatus(u) === 'Invited'
      ? [
          {
            label: 'Einladung erneut senden',
            icon: 'Mail' as const,
            onClick: () => handlers.onResend(u.id),
          },
        ]
      : []),
    ...(u.twoFactorEnabled && u.id !== currentUserId
      ? [
          {
            label: 'Zwei-Faktor zurücksetzen',
            icon: 'Shield' as const,
            destructive: true,
            onClick: () => handlers.onResetTwoFactor(u),
          },
        ]
      : []),
    u.isActive
      ? {
          label: 'Zugang sperren',
          icon: 'Lock' as const,
          destructive: true,
          onClick: () => handlers.onDeactivate(u.id),
        }
      : {
          label: 'Zugang freigeben',
          icon: 'Check' as const,
          onClick: () => handlers.onReactivate(u.id),
        },
  ];
}
